import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { translations } from '@/i18n/translations';

interface FooterProps {
  className?: string;
}

/**
 * Footer 组件
 * 展示页脚信息、版权声明、CTA 按钮和联系信息
 */
export function Footer({ className = '' }: FooterProps) {
  const { language, t } = useLanguage();
  const hero = translations[language].hero;

  const chromeStoreUrl = 'https://chromewebstore.google.com/detail/page-picker/jknigpbnkkmohofdfnangfffbjbomenn';
  const edgeStoreUrl = 'https://microsoftedge.microsoft.com/addons/detail/page-picker/aiglimalfnljcondcpinnbpakojheglj';

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <footer className={`bg-gray-900 text-gray-300 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* 产品信息 */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-white">Page Picker</h3>
            <p className="text-sm leading-relaxed">
              {t('footer.description')}
            </p>
          </div>

          {/* CTA 下拉按钮区域 */}
          <div className="flex flex-col items-start md:items-center justify-center space-y-3">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen((v) => !v)}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 hover:scale-105 shadow-md hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {t('footer.cta')}
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isDropdownOpen && (
                <div className="absolute bottom-full left-0 mb-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-20">
                  {/* Chrome 应用商店 */}
                  <a
                    href={chromeStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsDropdownOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-gray-800 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                  >
                    <img src="/chrome-icon.svg" alt="" className="w-5 h-5" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    <span className="font-medium">{hero.chromeStore}</span>
                  </a>

                  {/* Edge 应用商店 */}
                  <a
                    href={edgeStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsDropdownOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-gray-800 hover:bg-primary-50 hover:text-primary-600 transition-colors border-t border-gray-50"
                  >
                    <img src="/edge-icon.svg" alt="" className="w-5 h-5" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    <span className="font-medium">{hero.edgeStore}</span>
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* 联系与支持 */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white">
              {t('footer.contact')}
            </h4>
            <div className="text-sm space-y-2">
              <p>
                <a
                  href="mailto:pagepicker@163.com"
                  className="hover:text-primary-500 transition-colors duration-200"
                >
                  pagepicker@163.com
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* 版权信息 */}
        <div className="pt-8 border-t border-gray-800 text-center text-sm">
          <p>{t('footer.copyright')}</p>
        </div>
      </div>
    </footer>
  );
}